/**
 * Apply Template modal — 选模板 → 选版本 → 应用。
 */
import {useEffect, useState} from 'react';
import {ApiError} from '../lib/api';
import Modal from './Modal';
import {
  siteTemplateApi,
  templatesApi,
  type Template,
  type TemplateStatusResponse,
} from '../lib/api-resources';
import {friendlyError} from '../lib/error-labels';

interface TemplateApplyModalProps {
  siteId: string;
  onClose: () => void;
  /** Called after a successful apply; parent opens the job modal. */
  onApplied: (jobId: string) => void;
}

function capabilityList(caps: Record<string, boolean> | null) {
  if (!caps) return null;
  const keys = Object.keys(caps).filter((k) => caps[k] === true);
  if (keys.length === 0) return <span className="muted">（无）</span>;
  return (
    <div className="capability-list">
      {keys.map((k) => (
        <span key={k} className="capability-chip">{k}</span>
      ))}
    </div>
  );
}

export default function TemplateApplyModal({
  siteId,
  onClose,
  onApplied,
}: TemplateApplyModalProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [version, setVersion] = useState<string>('');
  const [status, setStatus] = useState<TemplateStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = templates.find((t) => t.template_id === selectedId);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    Promise.all([templatesApi.list(), siteTemplateApi.status(siteId)])
      .then(([t, s]) => {
        if (!mounted) return;
        setTemplates(t.templates);
        setStatus(s);
        if (t.templates.length > 0) {
          setSelectedId(t.templates[0].template_id);
        }
      })
      .catch((e) => {
        if (!mounted) return;
        setError(friendlyError(e instanceof ApiError ? e.code : null, String(e)).headline);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [siteId]);

  async function apply() {
    if (!selectedId) return;
    if (status?.template_id) {
      const ok = window.confirm(
        `当前已应用模板 ${status.template_id}@${status.template_version}，再次应用将覆盖现有内容。是否继续？`
      );
      if (!ok) return;
    }
    setApplying(true);
    setError(null);
    try {
      const r = await siteTemplateApi.apply(siteId, {
        template_id: selectedId,
        version: version || null,
        data: {},
        force: !!status?.template_id,
      });
      onApplied(r.job_id);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      setError(friendlyError(code, e instanceof Error ? e.message : String(e)).headline);
    } finally {
      setApplying(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="应用模板" width={720}>
      {loading ? (
        <div className="muted">正在加载模板…</div>
      ) : templates.length === 0 ? (
        <div className="alert alert-warn">
          还没有可用的模板。请先在「模板」页刷新或添加模板包。
        </div>
      ) : (
        <>
          {status?.template_id && (
            <div className="alert alert-info">
              当前已应用：<strong>{status.template_id}@{status.template_version}</strong>
              {status.last_job_status && (
                <span className="muted">（最近一次：{status.last_job_status}）</span>
              )}
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <div className="form-row">
            <label htmlFor="tpl-pick">选择模板</label>
            <select
              id="tpl-pick"
              value={selectedId}
              onChange={(e) => {
                setSelectedId(e.target.value);
                setVersion('');
              }}
            >
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.name}（{t.template_id}@{t.version}）
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label htmlFor="tpl-version">版本</label>
            <input
              id="tpl-version"
              type="text"
              placeholder="留空使用最新版本"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
            />
            <small className="muted">留空会自动选择最新版本。</small>
          </div>

          {selected && (
            <div className="template-preview">
              <h3>{selected.name}</h3>
              {selected.description && <p className="muted">{selected.description}</p>}
              <div className="template-preview-grid">
                <div>
                  <span className="muted">主题：</span> <code>{selected.theme_slug}</code>
                </div>
                <div>
                  <span className="muted">契约版本：</span> <code>{selected.contract_version}</code>
                </div>
                <div>
                  <span className="muted">SHA-256：</span>{' '}
                  <code title={selected.sha256}>{selected.sha256.slice(0, 24)}…</code>
                </div>
                <div>
                  <span className="muted">源路径：</span> <code>{selected.source_path}</code>
                </div>
              </div>
              <div className="template-preview-caps">
                <span className="muted">支持能力：</span>
                {capabilityList(selected.capabilities)}
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={applying}>
              取消
            </button>
            <button
              className="btn btn-primary"
              onClick={apply}
              disabled={!selectedId || applying}
            >
              {applying ? '正在应用…' : '应用模板'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
